import toast from 'react-hot-toast';
import {
  useNotificationStore,
  type NotificationKind,
} from '../store/useNotificationStore';
import { showMessageToast, type ToastAction } from '../components/ui/MessageToast';

const DEFAULT_DURATION_MS = 3000;
const DEFAULT_ERROR_DURATION_MS = 9000;
const REMOVAL_GRACE_MS = 200;

function stripHtml(s: string): string {
  return s.replace(/<[^>]*>/g, '');
}

function hashId(kind: NotificationKind, message: string): string {
  let h = 5381;
  const src = `${kind}:${message}`;
  for (let i = 0; i < src.length; i++) {
    h = ((h << 5) + h + src.charCodeAt(i)) | 0;
  }
  return `auto:${kind}:${(h >>> 0).toString(36)}`;
}

export interface NotifyOptions {
  id?: string;
  detail?: string | string[];
  duration?: number | null;
  /** Inline action button rendered in the toast (e.g. Undo). Not persisted to
   *  the notification store — a callback is only meaningful while the toast
   *  that carries it is on screen. */
  action?: ToastAction;
}

const pendingRemovals: Record<string, ReturnType<typeof setTimeout>> = {};

function emit(
  kind: NotificationKind,
  message: string,
  opts?: NotifyOptions,
): string {
  const cleaned = stripHtml(message);
  const detail = opts?.detail
    ? (Array.isArray(opts.detail) ? opts.detail : [opts.detail])
        .map(stripHtml)
        .join('\n')
    : undefined;
  const finalMessage = detail ? `${cleaned}\n\n${detail}` : cleaned;
  const id = opts?.id ?? hashId(kind, finalMessage);
  const duration =
    opts?.duration === undefined
      ? kind === 'error'
        ? DEFAULT_ERROR_DURATION_MS
        : DEFAULT_DURATION_MS
      : opts.duration;

  useNotificationStore.getState().upsert({
    id,
    kind,
    message: finalMessage,
    createdAt: Date.now(),
    duration,
  });

  // Render the branded banner (semantic color, close button, self-managed timer)
  // instead of react-hot-toast's default white pill. Sticky (duration === null)
  // stays until manually dismissed; finite durations auto-dismiss + show a bar.
  showMessageToast({ kind, message: finalMessage, id, duration, action: opts?.action });

  if (pendingRemovals[id]) {
    clearTimeout(pendingRemovals[id]);
    delete pendingRemovals[id];
  }
  if (duration !== null && Number.isFinite(duration)) {
    pendingRemovals[id] = setTimeout(() => {
      delete pendingRemovals[id];
      useNotificationStore.getState().remove(id);
    }, (duration as number) + REMOVAL_GRACE_MS);
  }

  return id;
}

function dismiss(id: string): void {
  toast.dismiss(id);
  if (pendingRemovals[id]) {
    clearTimeout(pendingRemovals[id]);
    delete pendingRemovals[id];
  }
  useNotificationStore.getState().remove(id);
}

function clear(): void {
  toast.dismiss();
  for (const id of Object.keys(pendingRemovals)) {
    clearTimeout(pendingRemovals[id]);
    delete pendingRemovals[id];
  }
  useNotificationStore.getState().clear();
}

export const notify = {
  success: (message: string, opts?: NotifyOptions) =>
    emit('success', message, opts),
  info: (message: string, opts?: NotifyOptions) => emit('info', message, opts),
  error: (
    message: string | string[],
    opts?: NotifyOptions & { mustDismiss?: boolean },
  ) => {
    const arr = Array.isArray(message) ? message : [message];
    const finalMessage = arr.length ? arr.join('\n') : 'An error occurred';
    const finalOpts: NotifyOptions = {
      ...opts,
      duration:
        opts?.mustDismiss === true
          ? null
          : opts?.duration !== undefined
            ? opts.duration
            : DEFAULT_ERROR_DURATION_MS,
    };
    return emit('error', finalMessage, finalOpts);
  },
  dismiss,
  clear,
};
